import React, { useState, useEffect, useMemo } from 'react';
import { dataService } from '../../services/dataService';
import {
  KINDS, WORKSPACE_SCOPE, availableScopes, defaultReviewDue,
  reviewState, validateKnowledge, sortForReview, summarise, groupByScope,
} from '../../utils/agentKnowledge';

const KIND_LABELS = {
  charter:   'Charter — who we are and what this team does',
  policy:    'Policy — a rule that must be followed',
  procedure: 'Procedure — how to do a recurring task',
  gotcha:    'Gotcha — something that catches people out',
  reference: 'Reference — background an agent may need',
};

const STATUS_BADGE = {
  proposed: { label: 'Awaiting review', color: '#b45309', bg: 'rgba(180,83,9,0.1)' },
  accepted: { label: 'In use',          color: '#0f766e', bg: 'rgba(15,118,110,0.1)' },
  retired:  { label: 'Retired',         color: '#64748b', bg: 'rgba(100,116,139,0.1)' },
};

const today = () => new Date().toISOString().slice(0, 10);

function Editor({ draft, setDraft, scopes, onSave, onCancel, errors }) {
  const set = (k, v) => setDraft(d => ({ ...d, [k]: v }));
  return (
    <div className="knowledge-editor">
      <div className="panel-field">
        <label className="panel-label">Title</label>
        <input
          className="panel-input"
          placeholder="e.g. Refunds requested after 90 days"
          value={draft.title}
          onChange={e => set('title', e.target.value)}
        />
        <span className="panel-hint" style={{ fontSize: 10 }}>
          Be specific. This is what an agent matches against when deciding whether to read it.
        </span>
      </div>

      <div className="panel-field">
        <label className="panel-label">Applies to</label>
        <select className="panel-input" value={draft.scope} onChange={e => set('scope', e.target.value)}>
          {scopes.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
        </select>
      </div>

      <div className="panel-field">
        <label className="panel-label">Kind</label>
        <select
          className="panel-input"
          value={draft.kind}
          onChange={e => {
            const kind = e.target.value;
            setDraft(d => ({ ...d, kind, reviewDue: defaultReviewDue(kind, d.learnedAt) }));
          }}
        >
          {KINDS.map(k => <option key={k} value={k}>{KIND_LABELS[k]}</option>)}
        </select>
      </div>

      <div className="panel-field">
        <label className="panel-label">What should the agent know?</label>
        <textarea
          className="panel-textarea"
          rows={6}
          value={draft.body}
          onChange={e => set('body', e.target.value)}
          placeholder="Write it as you would explain it to a new colleague on their first week."
        />
      </div>

      <div className="panel-field">
        <label className="panel-label">Review by</label>
        <input
          className="panel-input"
          type="date"
          value={draft.reviewDue || ''}
          onChange={e => set('reviewDue', e.target.value)}
        />
        <span className="panel-hint" style={{ fontSize: 10 }}>
          Knowledge that has quietly gone out of date is worse than none — it sends agents
          around a problem that was fixed. You will be reminded to re-check it.
        </span>
      </div>

      {errors.length > 0 && (
        <div className="knowledge-errors">
          {errors.map((e, i) => <div key={i}>{e}</div>)}
        </div>
      )}

      <div className="knowledge-editor-actions">
        <button className="panel-delete-btn" onClick={onCancel}>Cancel</button>
        <button className="panel-save-btn" style={{ width: 'auto', padding: '8px 20px' }} onClick={onSave}>
          Save
        </button>
      </div>
    </div>
  );
}

export default function KnowledgePanel({ workspaceId, nodes, canEdit, currentUser }) {
  const [docs, setDocs]       = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);   // docId | 'new' | null
  const [draft, setDraft]     = useState(null);
  const [errors, setErrors]   = useState([]);

  useEffect(() => {
    if (!workspaceId) { setDocs([]); setLoading(false); return; }
    setLoading(true);
    const unsub = dataService.subscribeKnowledge(workspaceId, d => { setDocs(d); setLoading(false); });
    return unsub;
  }, [workspaceId]);

  const scopes  = useMemo(() => availableScopes(nodes), [nodes]);
  const stats   = useMemo(() => summarise(docs, today()), [docs]);
  const groups  = useMemo(() => {
    const sorted = sortForReview(docs, today());
    return groupByScope(sorted, nodes);
  }, [docs, nodes]);

  const startNew = () => {
    setDraft({
      title: '', body: '', scope: WORKSPACE_SCOPE, kind: 'gotcha',
      status: 'accepted',                       // authored by a human, so already reviewed
      author: currentUser ? `user:${currentUser}` : 'user:unknown',
      learnedAt: today(),
      reviewDue: defaultReviewDue('gotcha', today()),
      reviewedBy: currentUser ? `user:${currentUser}` : null,
      reviewedAt: today(),
    });
    setErrors([]);
    setEditing('new');
  };

  const startEdit = (doc) => { setDraft({ ...doc }); setErrors([]); setEditing(doc.id); };

  const save = async () => {
    const errs = validateKnowledge(draft);
    if (errs.length) { setErrors(errs); return; }
    if (editing === 'new') await dataService.createKnowledge(workspaceId, draft);
    else await dataService.updateKnowledge(workspaceId, editing, draft);
    setEditing(null); setDraft(null);
  };

  const accept = (d) => dataService.setKnowledgeStatus(workspaceId, d.id, 'accepted', currentUser ? `user:${currentUser}` : null);
  const retire = (d) => dataService.setKnowledgeStatus(workspaceId, d.id, 'retired');
  const reopen = (d) => dataService.setKnowledgeStatus(workspaceId, d.id, 'accepted', currentUser ? `user:${currentUser}` : null);

  if (!workspaceId) return <div className="agents-page-empty"><p>Select a workspace.</p></div>;
  if (loading)      return <div className="agents-page-empty"><p>Loading knowledge…</p></div>;

  return (
    <div className="knowledge-panel">
      <div className="knowledge-header">
        <div>
          <h2 className="knowledge-title">What your agents know</h2>
          <p className="knowledge-sub">
            Agents read this before they act. Keep it small and current — every document is
            something an agent has to carry, and anything out of date will mislead it.
          </p>
        </div>
        {canEdit && <button className="agents-go-btn" onClick={startNew}>+ Add knowledge</button>}
      </div>

      <div className="knowledge-stats">
        <span><strong>{stats.accepted}</strong> in use</span>
        {stats.proposed > 0 && <span className="knowledge-stat-warn"><strong>{stats.proposed}</strong> awaiting review</span>}
        {stats.stale > 0 && <span className="knowledge-stat-warn"><strong>{stats.stale}</strong> overdue a re-check</span>}
        {stats.retired > 0 && <span><strong>{stats.retired}</strong> retired</span>}
      </div>

      {editing === 'new' && (
        <Editor draft={draft} setDraft={setDraft} scopes={scopes}
                onSave={save} onCancel={() => { setEditing(null); setDraft(null); }} errors={errors} />
      )}

      {docs.length === 0 && editing !== 'new' && (
        <div className="agents-page-empty">
          <div className="agents-empty-icon">📚</div>
          <p>Nothing here yet. Start with a charter — a short description of what your
             business does and how you expect an agent to behave.</p>
        </div>
      )}

      {groups.map(group => (
        <div key={group.scope} className="knowledge-group">
          <div className="knowledge-group-title">{group.label}</div>
          {group.docs.map(d => {
            const rs = reviewState(d, today());
            const badge = STATUS_BADGE[d.status] || STATUS_BADGE.accepted;
            return editing === d.id ? (
              <Editor key={d.id} draft={draft} setDraft={setDraft} scopes={scopes}
                      onSave={save} onCancel={() => { setEditing(null); setDraft(null); }} errors={errors} />
            ) : (
              <div key={d.id} className={`knowledge-card${d.status === 'retired' ? ' knowledge-retired' : ''}`}>
                <div className="knowledge-card-head">
                  <span className="knowledge-card-title">{d.title}</span>
                  <span className="knowledge-badge" style={{ color: badge.color, background: badge.bg }}>
                    {badge.label}
                  </span>
                </div>
                <div className="knowledge-card-meta">
                  {d.kind}
                  {d.author?.startsWith('agent:') && ` · proposed by ${d.author.slice(6)}`}
                  {rs === 'overdue'  && <span className="knowledge-stale"> · overdue a re-check</span>}
                  {rs === 'due-soon' && <span className="knowledge-stale"> · due a re-check soon</span>}
                </div>
                <div className="knowledge-card-body">{d.body}</div>
                {canEdit && (
                  <div className="knowledge-card-actions">
                    {d.status === 'proposed' && <button className="knowledge-btn-accept" onClick={() => accept(d)}>Accept</button>}
                    {d.status === 'proposed' && <button className="knowledge-btn" onClick={() => dataService.deleteKnowledgeProposal(workspaceId, d.id)}>Discard</button>}
                    {d.status === 'retired'  && <button className="knowledge-btn" onClick={() => reopen(d)}>Put back in use</button>}
                    <button className="knowledge-btn" onClick={() => startEdit(d)}>Edit</button>
                    {d.status === 'accepted' && <button className="knowledge-btn" onClick={() => retire(d)}>Retire</button>}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
}
